import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import type {
  CarSummaryDoc,
  DataNeed,
  FcyPeriodDoc,
  FuelDoc,
  LapDoc,
  LiveMessagesDoc,
  MessageDoc,
  StandingsDoc,
  PitStopDoc,
  StintsDoc,
  StreamSessionDoc,
} from '@racedash/shared'
import { isCaptureLive, ServerClock } from '@racedash/shared'
import { db } from '../lib/firebase'

/**
 * How many laps to hold for the car being watched.
 *
 * Enough for the largest lap list a widget will ask for, plus the lap in
 * progress. Not "all of them": a 25-hour race is several hundred per car, and
 * the only widget that wants the whole set is stint analysis, which is a page
 * rather than a tile.
 */
const lapWindow = 40

/**
 * How many to hold when a screen has asked for all of them.
 *
 * Not literally all: a bound is what stops a bucketing fault turning into a
 * browser that stops responding. Twelve hundred covers a twenty-five hour race
 * at Spa with room to spare, and anything past it is a fault rather than a long
 * race.
 */
const allLapsWindow = 1200

/** Race control rarely says more than a handful of things worth showing. */
const messageWindow = 40

export interface DashboardData {
  /**
   * Which bucket this data came from.
   *
   * Carried so a screen can open a listener of its own without being handed the
   * id separately — the lap chart comparing two cars needs laps this hook was
   * never asked for, and threading the id past every other screen to reach it
   * would be worse than putting it here.
   */
  sessionId: string | null
  session: StreamSessionDoc | null
  standings: StandingsDoc | null
  /** The watched car's laps, most recent first. */
  laps: LapDoc[]
  /** Race-director messages, most recent first. The log. */
  messages: MessageDoc[]
  /**
   * What race control is showing *now*, which the log cannot answer: nothing
   * records when a message left the board, so age alone cannot tell a live
   * message from an hour-old one.
   */
  liveMessages: LiveMessagesDoc | null
  /** Every car's stints, materialised by the interpreter. */
  stints: StintsDoc[]
  /** Completed pit stops for the whole field. */
  pitStops: PitStopDoc[]
  /** Neutralisations, for the stint chart's track-state band. */
  fcyPeriods: FcyPeriodDoc[]
  /**
   * The watched car's session bests: best lap, best of each sector, ideal.
   *
   * One document rather than the whole collection, because a tile comparing
   * this car against itself has no use for the rest of the field.
   */
  carSummary: CarSummaryDoc | null
  /** Refuelling for the whole field, as the fuel job matched it to stops. */
  fuel: FuelDoc[]
  /**
   * Who the organisation says is in each car, by car number.
   *
   * The overlay wins over the vendor where it exists — see §13. Empty until the
   * declaration write path has a screen behind it; the read side is here so the
   * timing table can already show the distinction.
   */
  declaredDrivers?: Map<string, string>
  /** The clock the checkpoints were written against, not the viewer's own. */
  clock: ServerClock
  /**
   * The instant everything on this dashboard is measured to.
   *
   * While capture is live this is clock.now() — the viewer's clock corrected
   * against the one the timing was written on. Once capture has stopped it is
   * the end of the session, and stops moving.
   *
   * That second half is the point. Every elapsed figure on a dashboard —
   * time since the last lap, how long the current driver has been out, how far
   * into a stint the car is — is measured from something in the past to "now",
   * and if "now" keeps advancing after the race has finished then so do all of
   * them. A car that retired in May reads as having been out for three months.
   *
   * The race clock already stops, because the vendor tells it to. This is the
   * same behaviour for everything the vendor does not.
   */
  asAt: (browserNow: Date) => Date
  /**
   * Anything a listener refused to do.
   *
   * Surfaced rather than folded into an empty result. A missing composite index
   * fails exactly like a session with no laps, and last time that cost an
   * afternoon: the screen said "no sessions" and meant "I was not allowed to
   * ask".
   */
  errors: string[]
}

/**
 * Open one listener per distinct thing the widgets need, and no more.
 *
 * A dashboard of twelve tiles is still four listeners at most, because each
 * widget declares what it reads rather than fetching for itself. On a pit-wall
 * connection that is one reconnection to think about instead of twelve.
 */
export function useSessionData(
  streamSessionId: string | null,
  car: string | null,
  needs: readonly DataNeed[],
): DashboardData {
  const [session, setSession] = useState<StreamSessionDoc | null>(null)
  const [standings, setStandings] = useState<StandingsDoc | null>(null)
  const [laps, setLaps] = useState<LapDoc[]>([])
  const [messages, setMessages] = useState<MessageDoc[]>([])
  const [liveMessages, setLiveMessages] = useState<LiveMessagesDoc | null>(null)
  const [stints, setStints] = useState<StintsDoc[]>([])
  const [pitStops, setPitStops] = useState<PitStopDoc[]>([])
  const [fcyPeriods, setFcyPeriods] = useState<FcyPeriodDoc[]>([])
  const [carSummary, setCarSummary] = useState<CarSummaryDoc | null>(null)
  const [fuel, setFuel] = useState<FuelDoc[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  // One instance for the life of the view: it accumulates samples, and a fresh
  // one each render would never have any.
  const clockRef = useRef<ServerClock | null>(null)
  if (clockRef.current === null) clockRef.current = new ServerClock()
  const clock = clockRef.current

  // But not across sessions. The samples describe one bucket's capture, and a
  // view that moves from a live session to an archived one would otherwise keep
  // correcting against the live one it has left.
  const sampledFor = useRef<string | null>(null)
  if (sampledFor.current !== streamSessionId) {
    sampledFor.current = streamSessionId
    clock.reset()
  }

  // Primitives, so a caller passing a fresh array every render does not
  // resubscribe every render.
  const wantSession = needs.includes('session')
  const wantStandings = needs.includes('standings')
  const wantLaps = needs.includes('laps')
  const wantAllLaps = needs.includes('allLaps')
  const wantMessages = needs.includes('messages')
  const wantLiveMessages = needs.includes('liveMessages')
  const wantStints = needs.includes('stints')
  const wantPitStops = needs.includes('pitStops')
  const wantFcy = needs.includes('fcy')
  const wantCarSummary = needs.includes('carSummary')
  const wantFuel = needs.includes('fuel')

  const fail = (key: string) => (e: Error) =>
    setErrors((prev) => (prev[key] === e.message ? prev : { ...prev, [key]: e.message }))
  // Forgotten when the listener that raised it is replaced. A refused listener
  // is never retried, so the only way back from a refusal is a fresh
  // subscription — and an error that outlived one went on reporting a failure
  // the page had recovered from. That was the share link's fault on Saturday,
  // fixed in useEventSessions and not here.
  const clear = (key: string) =>
    setErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })

  /*
   * Every effect below starts by forgetting what it held.
   *
   * Not only when it has nothing to subscribe to. When the session or the car
   * changes, the old listener is torn down and a new one opened, and between
   * the two the state still holds the previous session's standings, stints and
   * messages — shown under the new session's name until the first snapshot
   * lands. On a car change the sectors tile read the old car's split times
   * against the new car's number. Clearing first makes the gap read as
   * "loading" rather than as a wrong answer.
   */

  useEffect(() => {
    setSession(null)
    clear('session')
    if (!streamSessionId || !wantSession) return
    return onSnapshot(
      doc(db, 'streamSessions', streamSessionId),
      (snap) => {
        const data = snap.exists() ? (snap.data() as StreamSessionDoc) : null
        setSession(data)
        /*
         * Every poll restamps lastSeenAt, so on a live session this is a free
         * and continuous reading of the clock the checkpoints were written
         * against.
         *
         * On an archived one it is not a clock reading at all, it is the age of
         * the racing, and offering it would drag "now" back to whenever capture
         * stopped. ServerClock refuses samples that far out on its own; asking
         * here as well keeps the intent visible at the place the decision is
         * actually made.
         */
        const lastSeen = data?.lastSeenAt?.toDate()
        if (lastSeen && isCaptureLive(lastSeen, new Date())) clock.sample(lastSeen)
      },
      fail('session'),
    )
  }, [streamSessionId, wantSession, clock])

  useEffect(() => {
    setStandings(null)
    clear('standings')
    if (!streamSessionId || !wantStandings) return
    return onSnapshot(
      doc(db, 'streamSessions', streamSessionId, 'live', 'standings'),
      (snap) => setStandings(snap.exists() ? (snap.data() as StandingsDoc) : null),
      fail('standings'),
    )
  }, [streamSessionId, wantStandings])

  useEffect(() => {
    setLaps([])
    clear('laps')
    if (!streamSessionId || (!wantLaps && !wantAllLaps) || !car) return
    // One listener either way. A dashboard with both a lap-list tile and a lap
    // times page asks for the wider window once rather than opening two
    // subscriptions over the same documents.
    const q = query(
      collection(db, 'streamSessions', streamSessionId, 'laps'),
      where('car', '==', car),
      orderBy('lap', 'desc'),
      limit(wantAllLaps ? allLapsWindow : lapWindow),
    )
    // Live, not fetched once: a lap completed while the page is open has to
    // appear in the table and move the averages with it, which is most of why
    // anybody leaves this open during a race.
    return onSnapshot(q, (snap) => setLaps(snap.docs.map((d) => d.data() as LapDoc)), fail('laps'))
  }, [streamSessionId, wantLaps, wantAllLaps, car])

  useEffect(() => {
    setMessages([])
    clear('messages')
    if (!streamSessionId || !wantMessages) return
    const q = query(
      collection(db, 'streamSessions', streamSessionId, 'messages'),
      orderBy('receivedAt', 'desc'),
      limit(messageWindow),
    )
    return onSnapshot(
      q,
      (snap) => setMessages(snap.docs.map((d) => d.data() as MessageDoc)),
      fail('messages'),
    )
  }, [streamSessionId, wantMessages])

  useEffect(() => {
    setLiveMessages(null)
    clear('liveMessages')
    if (!streamSessionId || !wantLiveMessages) return
    return onSnapshot(
      doc(db, 'streamSessions', streamSessionId, 'live', 'messages'),
      (snap) => setLiveMessages(snap.exists() ? (snap.data() as LiveMessagesDoc) : null),
      fail('liveMessages'),
    )
  }, [streamSessionId, wantLiveMessages])

  useEffect(() => {
    setStints([])
    clear('stints')
    if (!streamSessionId || !wantStints) return
    // One document per car, so the whole chart is one listener over ~30
    // documents rather than a query across every lap of the session.
    return onSnapshot(
      collection(db, 'streamSessions', streamSessionId, 'stints'),
      (snap) => setStints(snap.docs.map((d) => d.data() as StintsDoc)),
      fail('stints'),
    )
  }, [streamSessionId, wantStints])

  useEffect(() => {
    setFuel([])
    clear('fuel')
    if (!streamSessionId || !wantFuel) return
    // The whole field: a few fills per car per session, so tens of documents,
    // and the screen lists everybody's.
    return onSnapshot(
      collection(db, 'streamSessions', streamSessionId, 'fuel'),
      (snap) => setFuel(snap.docs.map((d) => d.data() as FuelDoc)),
      fail('fuel'),
    )
  }, [streamSessionId, wantFuel])

  useEffect(() => {
    setPitStops([])
    clear('pitStops')
    if (!streamSessionId || !wantPitStops) return
    // The whole field, not one car. Driving time needs this car's stops, but a
    // stop-by deadline is read against the field's pace, and the collection is
    // a few hundred documents for a long race rather than a few thousand.
    return onSnapshot(
      collection(db, 'streamSessions', streamSessionId, 'pitStops'),
      (snap) => setPitStops(snap.docs.map((d) => d.data() as PitStopDoc)),
      fail('pitStops'),
    )
  }, [streamSessionId, wantPitStops])

  useEffect(() => {
    setFcyPeriods([])
    clear('fcy')
    if (!streamSessionId || !wantFcy) return
    return onSnapshot(
      collection(db, 'streamSessions', streamSessionId, 'fcyPeriods'),
      (snap) => setFcyPeriods(snap.docs.map((d) => d.data() as FcyPeriodDoc)),
      fail('fcy'),
    )
  }, [streamSessionId, wantFcy])

  useEffect(() => {
    setCarSummary(null)
    clear('carSummary')
    if (!streamSessionId || !wantCarSummary || !car) return
    return onSnapshot(
      doc(db, 'streamSessions', streamSessionId, 'carSummaries', car),
      // Absent until the car has completed a lap, which is not a failure: the
      // tile shows nothing to compare against, because there is nothing yet.
      (snap) => setCarSummary(snap.exists() ? (snap.data() as CarSummaryDoc) : null),
      fail('carSummary'),
    )
  }, [streamSessionId, wantCarSummary, car])

  const errorList = useMemo(() => Object.values(errors), [errors])

  /*
   * Measured against the BROWSER's clock, deliberately.
   *
   * ServerClock takes its offset from lastSeenAt so that race times read
   * correctly on a viewer's wrong clock — which means that on a session
   * captured in May it reports a "now" in May, and lastSeenAt measured against
   * it looks seconds old for ever. Asking it whether capture is live would
   * always hear yes.
   */
  const asAt = useCallback(
    (browserNow: Date) => {
      // The flag, where the stream gave us one.
      const finished = session?.finishedAt?.toDate() ?? null
      if (finished) return finished

      /*
        Otherwise the session's own clock, which is the thing a person watching
        already believes. If the countdown has stopped then race time has
        stopped, and everything measured in race time should stop with it.

        This deliberately covers a red flag, when the clock stops and the
        session is not over. Under a red flag the cars are stationary, so a
        figure that kept climbing would be counting something nobody is doing —
        and it is indistinguishable, on screen, from a page that has frozen.

        It also covers the case that prompted this: a session red-flagged with
        four minutes left and never restarted. No countdown reached zero and the
        stream never said FINISHED, so there is no flag to find — but the clock
        stopped, and that is enough.

        Keying this on capture instead, which is what it used to do, was wrong
        for a reason already written down elsewhere in this repo: capture runs
        for the whole window, which continues long after the racing stops. Nine
        hours of it, at Magny Cours.
      */
      const cp = session?.clock
      if (cp?.at && !cp.running) return cp.at.toDate()

      const lastSeen = session?.lastSeenAt?.toDate() ?? null
      if (isCaptureLive(lastSeen, browserNow)) return clock.now(browserNow)
      return lastSeen ?? clock.now(browserNow)
    },
    [session, clock],
  )

  return {
    sessionId: streamSessionId,
    session,
    standings,
    laps,
    messages,
    liveMessages,
    stints,
    pitStops,
    fcyPeriods,
    carSummary,
    fuel,
    clock,
    asAt,
    errors: errorList,
  }
}

/**
 * A shared heartbeat.
 *
 * One timer for the whole dashboard rather than one per widget: the clock and
 * the countdown have to agree to the second, and twelve independent intervals
 * would drift apart visibly.
 */
export function useNow(periodMs = 1000): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), periodMs)
    return () => clearInterval(id)
  }, [periodMs])
  return now
}
